import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../00-MAIN/00-CONTEXT/AuthContext";

const USER_BACKEND_API_BASE_URL = process.env.REACT_APP_USER_BACKEND_API_URL;

function LoginPage() {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading, login, initialCheckComplete } = useAuth();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);

  // Redirection si déjà authentifié - APRÈS la vérification initiale
  useEffect(() => {
    if (initialCheckComplete && isAuthenticated) {
      navigate("/home", { replace: true });
    }
  }, [isAuthenticated, initialCheckComplete, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    const isValidEmail = /\S+@\S+\.\S+/.test(identifier);
    const isValidPhone = /^\d{10}$/.test(identifier);

    if (!(isValidEmail || isValidPhone)) {
      setError("Veuillez entrer un email ou un numéro de téléphone valide.");
      setIsErrorModalOpen(true);
      setLoading(false);
      return;
    }

    const payload = {
      password,
      ...(isValidEmail ? { email: identifier } : { telephone: identifier }),
    };

    try {
      const response = await fetch(`${USER_BACKEND_API_BASE_URL}/users/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const { error: msg } = await response.json();
        setError(msg || "Email/téléphone ou mot de passe incorrect.");
        setIsErrorModalOpen(true);
        setLoading(false);
        return;
      }

      const responseData = await response.json();
      const { access_token, user: userData } = responseData;
      
      // Utiliser la fonction login du contexte
      await login(access_token, userData);
      
      // Navigation après mise à jour du contexte
      navigate("/home", { replace: true });
      
    } catch (err) {
      setError("Une erreur réseau est survenue. Vérifiez votre connexion et réessayez.");
      setIsErrorModalOpen(true);
      setLoading(false);
    }
  };

  // Affichage du loading pendant la vérification d'auth initiale
  if (authLoading || !initialCheckComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <p>Vérification de l'authentification...</p>
      </div>
    );
  }

  // Si déjà authentifié, ne pas afficher le formulaire
  if (isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <p>Redirection en cours...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
      <div className="bg-white p-8 rounded shadow-md w-full max-w-md">
        <h2 className="text-2xl font-bold mb-6 text-center">Se connecter</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="identifier" className="block text-gray-700 mb-1">
              Email ou Téléphone
            </label>
            <input
              type="text"
              id="identifier"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              className="w-full border border-gray-300 p-2 rounded"
              placeholder="user@exemple.com ou 0600000000"
              required
            />
          </div>
          <div className="mb-6">
            <label htmlFor="password" className="block text-gray-700 mb-1">
              Mot de passe
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full border border-gray-300 p-2 rounded"
              required
            />
            <div className="mt-1 text-right">
              <span
                onClick={() => navigate("/forgot-password")}
                className="text-[#00675A] text-sm cursor-pointer hover:underline"
              >
                Mot de passe oublié ?
              </span>
            </div>
          </div>
          <button
            type="submit"
            className="w-full bg-[#00675A] text-white py-2 rounded hover:bg-[#00584e] transition-colors"
            disabled={loading}
          >
            {loading ? "Connexion en cours..." : "Se connecter"}
          </button>
        </form>
        <div className="mt-4 text-center text-gray-600 text-sm">
          Vous n'avez pas de compte ?{" "}
          <span
            onClick={() => navigate("/signup")}
            className="text-[#00675A] cursor-pointer hover:underline"
          >
            Créer un compte
          </span>
        </div>
      </div>

      <Transition appear show={isErrorModalOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-10"
          onClose={() => setIsErrorModalOpen(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 shadow-xl transition-all">
                  <div className="flex justify-between items-center">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-medium leading-6 text-gray-900"
                    >
                      Erreur de connexion
                    </Dialog.Title>
                    <button
                      type="button"
                      className="text-gray-400 hover:text-gray-500 text-2xl font-semibold"
                      onClick={() => setIsErrorModalOpen(false)}
                    >
                      &times;
                    </button>
                  </div>
                  <div className="mt-4">
                    <p className="text-sm text-gray-500">{error}</p>
                  </div>
                  <div className="mt-6 text-right">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-[#00675A] px-4 py-2 text-sm font-medium text-white hover:bg-[#00584e] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                      onClick={() => setIsErrorModalOpen(false)}
                    >
                      OK
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}

export default LoginPage;